import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import {
  TitleMedium,
  TitleLarge,
  ParagraphMediumText,
  ParagraphSmallText,
  BodyLarge,
  Heading5Text,
  ParagraphLargeText,
} from '../../../components/Texts';
import TextInput, { TextInputContainer } from '../../../components/Inputs/Text';
import Button from '../../../components/Button';
import {
  useGetGiftCardCatalogueQuery,
  useGetGiftCardByIdQuery,
} from '../../../redux/api/public/globetopper';
import { useEstimatePaymentQuery } from '../../../redux/api/authorized/globetopper';
import { getCurrencySymbol } from '../../../common/currency';
import {
  selectGiftCardById,
  selectSelectedCountryCode,
} from '../../../redux/slices/giftCardCatalogue';
import { selectPrivyToken } from '../../../redux/slices/auth';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import {
  sendEvent,
  EVENT_SPEND_DETAIL_AMOUNT_CHANGED,
  EVENT_SPEND_DETAIL_BUY_BUTTON_CLICKED,
} from '../../../common/analytics';
import { NavigationBackButton } from '../../../components/BackButton/NavigationBackButton';
import ScrollRestore from '../../../components/ScrollRestore';
import useCustomLogin from '../../../hooks/useCustomLogin';
import Selector from '../../../components/Inputs/Select';
import CashbackInfo from '../../../components/CashbackInfo';

const MAX_CHARACTERS_LENGTH = 200;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  @media ${PHONE_MEDIA_QUERY} {
    position: relative;
  }
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  margin-top: 16px;
`;

const CardContainer = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  gap: 0;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 24px;
  }
`;

const ImageSection = styled.div`
  display: flex;
  width: 45%;
  min-width: 400px;
  height: fit-content;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.background.secondary || '#f5f5f5'};
  padding: 60px;
  flex-shrink: 0;
  border-radius: 16px;

  @media ${PHONE_MEDIA_QUERY} {
    display: none;
  }
`;

const DesktopBrandImage = styled.img`
  max-width: 100%;
  max-height: 600px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding: 0px 40px;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 0 20px 20px 20px;
    gap: 8px;
  }
`;

const BrandHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 8px;

  @media ${PHONE_MEDIA_QUERY} {
    align-items: flex-start;
    margin-top: 140px;
  }
`;

const BrandImageContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.background.secondary};
  display: none;

  @media ${PHONE_MEDIA_QUERY} {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    background-color: ${({ theme }) => theme.background.secondary || '#f5f5f5'};
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
    padding: 20px 0px;
    box-sizing: border-box;
    top: 0;
  }
`;

const BrandImageWrapper = styled.div`
  @media ${PHONE_MEDIA_QUERY} {
    position: relative;
    width: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const BrandImage = styled.img`
  width: auto;
  max-width: 175px;
  max-height: 200px;
  height: auto;
  object-fit: contain;
  border-radius: 0;
  background-color: transparent;
`;

const BrandInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  align-items: flex-start;
  margin-top: -16px;
`;

const UsageBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Badge = styled.span`
  padding: 6px 14px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.primary.background};
  color: ${({ theme }) => theme.primary.primary};
  font-size: 12px;
  font-weight: 500;
  border: 1px solid
    ${({ theme }) => theme.primary.lighter || theme.primary.background};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PriceDisplay = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  margin-top: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExpandableSection = styled.details`
  padding-top: ${({ $isFirst }) => ($isFirst ? '0' : '4px')};
  border-top: ${({ $isFirst, theme }) =>
    $isFirst
      ? 'none'
      : `1px solid ${theme.border.primary || 'rgba(0, 0, 0, 0.08)'}`};

  &[open] summary::before {
    transform: rotate(90deg);
  }
`;

const ExpandableSummary = styled.summary`
  cursor: pointer;
  color: ${({ theme }) => theme.text.title};
  font-size: 15px;
  font-weight: 600;
  padding: 8px 0;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &::before {
    content: '▶';
    display: inline-block;
    margin-right: 8px;
    transition: transform 0.2s;
  }
`;

const AmountInputWrapper = styled.div`
  position: relative;
  width: 50%;
  min-width: 250px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
    min-width: unset;
  }
`;

const CurrencyPrefix = styled(ParagraphLargeText)`
  position: absolute;
  left: 15px;
  top: 24px;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.text.title};
  margin: 0;
  pointer-events: none;
  z-index: 1;
`;

const StyledTextInputWrapper = styled.div`
  width: 100%;

  /* Target the TextInputContainer that's nested inside */
  ${TextInputContainer} {
    padding-left: 40px !important;
  }
`;

const AmountDisplay = styled.div`
  display: flex;
  gap: 12px;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
  }

  div:first-child {
    @media ${PHONE_MEDIA_QUERY} {
      width: 100%;
      min-width: unset;
    }
  }
`;

const ExpandableContent = styled.div`
  white-space: pre-line;
  color: ${({ theme }) => theme.text.regular};
  font-size: 14px;
  line-height: 1.7;
`;

const TermsLink = styled.a`
  color: ${({ theme }) => theme.primary.primary};
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const DescriptionWrapper = styled.div`
  position: relative;
  ${({ $isTruncated, theme }) =>
    $isTruncated
      ? `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 80px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        ${theme.background.primary || '#ffffff'} 100%
      );
      pointer-events: none;
    }
  `
      : ''}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const UsdEstimate = styled(ParagraphSmallText)`
  color: ${({ theme }) => theme.text.secondary};
  margin-top: 8px;
  font-style: italic;
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

// Helper function to extract range info from card data
const getDenominationRange = (card) => {
  if (!card) return null;

  // First, try to use detailedInfo if available (preferred method)
  if (
    card.detailedInfo?.min &&
    card.detailedInfo?.max &&
    card.detailedInfo?.increment
  ) {
    return {
      min: parseFloat(card.detailedInfo.min.replace(/,/g, '')),
      max: parseFloat(card.detailedInfo.max.replace(/,/g, '')),
      step: parseFloat(card.detailedInfo.increment.replace(/,/g, '')),
    };
  }

  // Fall back to parsing denomination string
  if (!card.denomination) return null;

  // Handle "or" clauses - take the first range
  const firstRange = card.denomination.split(' or ')[0];

  // Extract min, max, and step
  const match = firstRange.match(
    /(\d+\.?\d*)\s*-\s*(\d+\.?\d*)\s*by\s*(\d+\.?\d*)/,
  );
  if (!match) return null;

  const min = parseFloat(match[1].replace(/,/g, ''));
  const max = parseFloat(match[2].replace(/,/g, ''));
  const step = parseFloat(match[3]);

  return { min, max, step };
};

// Helper to check if string is a URL
const isUrl = (str) => {
  if (!str) return false;
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

const SpendGiftCardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const selectedCountryCode = useSelector(selectSelectedCountryCode);
  const privyToken = useSelector(selectPrivyToken);
  const { login } = useCustomLogin();
  const [initialAmountConfigured, setInitialAmountConfigured] = useState(false);

  // Get country from Redux to fetch the correct country-specific cards
  const countryCode = selectedCountryCode || '';

  // Use country code from URL to fetch the correct country's cards
  useGetGiftCardCatalogueQuery(countryCode);

  const selectedCard = useSelector((state) =>
    selectGiftCardById(state, countryCode, id),
  );

  const { data: fallbackCard } = useGetGiftCardByIdQuery(id, {
    skip: !!selectedCard || !id,
  });

  const card = selectedCard || fallbackCard;

  // Get denomination range info from card data
  const denominationRange = useMemo(() => {
    if (!card) return null;
    return getDenominationRange(card);
  }, [card]);

  const [selectedAmount, setSelectedAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Parse usage string into badges
  const usageBadges = useMemo(() => {
    if (!card?.usage) return [];
    return card.usage.split(',').map((u) => u.trim());
  }, [card]);

  useEffect(() => {
    const checkoutAmount = searchParams.get('checkoutAmount');
    if (privyToken && checkoutAmount) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('checkoutAmount');
      setSearchParams(newSearchParams, { replace: true });

      navigate(`/app/spend/${id}/checkout?amount=${checkoutAmount}&quantity=1`);
    }
  }, [privyToken, searchParams, setSearchParams, id, navigate]);

  const currencyCode = card?.detailedInfo?.currency?.code || card?.currency;
  const currencySymbol = getCurrencySymbol(currencyCode);
  const isUsdCurrency = currencyCode?.toUpperCase() === 'USD';

  // Validate amount input
  const validateAmount = (value) => {
    if (!value || value.trim() === '') {
      setAmountError('');
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      setAmountError('Please enter a valid number');
      return;
    }

    if (!denominationRange) {
      setAmountError('');
      return;
    }

    const { min, max, step } = denominationRange;

    if (numValue < min) {
      setAmountError(`Minimum amount is ${currencySymbol}${min.toFixed(2)}`);
      return;
    }

    if (numValue > max) {
      setAmountError(`Maximum amount is ${currencySymbol}${max.toFixed(2)}`);
      return;
    }

    // Check if value is a valid step (allowing for floating point precision)
    const remainder = (numValue - min) % step;
    const tolerance = 0.0001; // Small tolerance for floating point errors
    if (remainder > tolerance && step - remainder > tolerance) {
      setAmountError(
        `Amount must be in increments of ${currencySymbol}${step.toFixed(2)}`,
      );
      return;
    }

    setAmountError('');
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setSelectedAmount(value);
    validateAmount(value);

    // Track amount change if it's a valid number
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && value.trim() !== '') {
      sendEvent(EVENT_SPEND_DETAIL_AMOUNT_CHANGED, {
        gift_card_id: card?.id,
        gift_card_brand: card?.brand,
        amount: numValue,
        currency: card?.currency,
      });
    }
  };

  const handleAmountBlur = () => {
    validateAmount(selectedAmount);
  };

  const handleFixedAmountSelect = (amount) => {
    const amountStr = amount.toString();
    setSelectedAmount(amountStr);
    setAmountError('');

    const numValue = parseFloat(amount);
    if (!isNaN(numValue)) {
      sendEvent(EVENT_SPEND_DETAIL_AMOUNT_CHANGED, {
        gift_card_id: card?.id,
        gift_card_brand: card?.brand,
        amount: numValue,
        currency: card?.currency,
      });
    }
  };

  const isFixedType = card?.value_type === 'Fixed';
  const fixedAmounts =
    isFixedType && card?.detailedInfo?.display
      ? card.detailedInfo.display.map((amt) => parseFloat(amt))
      : [];

  const numericAmount = parseFloat(selectedAmount);
  const isValidAmount = isFixedType
    ? selectedAmount &&
      !isNaN(numericAmount) &&
      fixedAmounts.includes(numericAmount)
    : selectedAmount &&
      !amountError &&
      !isNaN(numericAmount) &&
      denominationRange &&
      numericAmount >= denominationRange.min &&
      numericAmount <= denominationRange.max;

  const selectedAmountLabel = isValidAmount
    ? `${currencySymbol}${numericAmount.toFixed(2)}`
    : '';

  // Fetch USD estimate when amount is valid and currency is not USD
  const { data: estimateData, isFetching: isEstimatingEstimate } =
    useEstimatePaymentQuery(
      {
        giftCardId: card?.id?.toString(),
        localAmount:
          isValidAmount && numericAmount ? numericAmount.toFixed(2) : undefined,
      },
      {
        skip:
          !card ||
          !isValidAmount ||
          !numericAmount ||
          isUsdCurrency ||
          !privyToken,
      },
    );

  useEffect(() => {
    if (!initialAmountConfigured) {
      if (isFixedType) {
        if (fixedAmounts.length > 0) {
          const middleIndex = Math.floor((fixedAmounts.length - 1) / 2);
          setSelectedAmount(fixedAmounts[middleIndex].toFixed(2));
          setInitialAmountConfigured(true);
        }
      } else {
        if (denominationRange) {
          setInitialAmountConfigured(true);
        }
      }
    }
  }, [
    denominationRange,
    isFixedType,
    fixedAmounts,
    initialAmountConfigured,
    setInitialAmountConfigured,
  ]);

  if (!card) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate('/app/spend')} />
        <CardContainer>
          <TitleMedium style={{ color: theme.text.title }}>
            Gift Card Not Found
          </TitleMedium>
          <ParagraphMediumText style={{ color: theme.text.secondary }}>
            The gift card with ID {id} could not be found.
          </ParagraphMediumText>
        </CardContainer>
      </Container>
    );
  }

  return (
    <Container>
      <ScrollRestore />
      <CardContainer>
        {/* Desktop Image Section */}
        {card.card_image && (
          <ImageSection>
            <DesktopBrandImage src={card.card_image} alt={card.brand} />
          </ImageSection>
        )}

        {/* Content Section */}
        <ContentSection>
          {/* Brand Header */}
          <BrandHeader>
            <BrandImageContainer>
              <BrandImageWrapper>
                {card.card_image && (
                  <BrandImage src={card.card_image} alt={card.brand} />
                )}
              </BrandImageWrapper>
            </BrandImageContainer>
            <NavigationBackButton onClick={() => navigate('/app/spend')} />
            <BrandInfo>
              <TitleLarge $variant='text-title' style={{ margin: 0 }}>
                {card.brand}
              </TitleLarge>
              {usageBadges.length > 0 && (
                <UsageBadges>
                  {usageBadges.map((usage, index) => (
                    <Badge key={index}>{usage}</Badge>
                  ))}
                </UsageBadges>
              )}
            </BrandInfo>
          </BrandHeader>

          {/* Description */}
          {card.brand_description && (
            <Section>
              <DescriptionWrapper
                $isTruncated={
                  card.brand_description.length > MAX_CHARACTERS_LENGTH &&
                  !isDescriptionExpanded
                }
              >
                <BodyLarge
                  $variant='text-regular'
                  style={{ lineHeight: '1.6', whiteSpace: 'pre-line' }}
                >
                  {card.brand_description.length > MAX_CHARACTERS_LENGTH &&
                  !isDescriptionExpanded
                    ? card.brand_description.substring(0, MAX_CHARACTERS_LENGTH)
                    : card.brand_description}
                </BodyLarge>
              </DescriptionWrapper>
              {card.brand_description.length > MAX_CHARACTERS_LENGTH && (
                <SeeMoreButton
                  onClick={() =>
                    setIsDescriptionExpanded(!isDescriptionExpanded)
                  }
                >
                  {isDescriptionExpanded ? 'See less' : 'See more'}
                </SeeMoreButton>
              )}
            </Section>
          )}

          {/* Amount Selection */}
          <Section>
            <Heading5Text $variant='text-title' style={{ margin: 0 }}>
              Enter amount
            </Heading5Text>
            <AmountDisplay>
              {isFixedType && fixedAmounts.length > 0 ? (
                <ButtonWrapper style={{ width: '100%' }}>
                  <Selector
                    options={fixedAmounts.map((amount) => ({
                      value: amount.toFixed(2),
                      label: `${currencySymbol} ${amount.toFixed(2)}`,
                    }))}
                    value={selectedAmount}
                    onChange={handleFixedAmountSelect}
                    placeholder='Select amount'
                  />
                </ButtonWrapper>
              ) : (
                <>
                  {denominationRange ? (
                    <>
                      <AmountInputWrapper>
                        <CurrencyPrefix $variant='text-title'>
                          {currencySymbol}
                        </CurrencyPrefix>
                        <StyledTextInputWrapper>
                          <TextInput
                            type='number'
                            value={selectedAmount}
                            onChange={handleAmountChange}
                            onBlur={handleAmountBlur}
                            placeholder={`${denominationRange.min.toFixed(2)} - ${denominationRange.max.toFixed(2)}`}
                            error={amountError}
                            inputMode='decimal'
                          />
                        </StyledTextInputWrapper>
                      </AmountInputWrapper>
                    </>
                  ) : (
                    <ParagraphMediumText $variant='text-regular'>
                      Amount options not available
                    </ParagraphMediumText>
                  )}
                </>
              )}
              {isMobile && !isUsdCurrency && isValidAmount && numericAmount && (
                <UsdEstimate $variant='text-secondary'>
                  {isEstimatingEstimate
                    ? 'Calculating USD estimate...'
                    : estimateData?.usdAmount ||
                        estimateData?.paymentTokenAmount
                      ? `≈ $${parseFloat(estimateData.usdAmount || estimateData.paymentTokenAmount).toFixed(2)} USD`
                      : null}
                </UsdEstimate>
              )}
              <ButtonWrapper>
                <Button
                  variant='primary'
                  fullWidth
                  onClick={() => {
                    if (!isValidAmount) return;

                    sendEvent(EVENT_SPEND_DETAIL_BUY_BUTTON_CLICKED, {
                      gift_card_id: card.id,
                      gift_card_brand: card.brand,
                      amount: numericAmount,
                      currency: card.currency,
                    });

                    if (privyToken) {
                      navigate(
                        `/app/spend/${id}/checkout?amount=${numericAmount}&quantity=1`,
                      );
                    } else {
                      // User not authenticated, add amount to URL and trigger login
                      const newSearchParams = new URLSearchParams(searchParams);
                      newSearchParams.set(
                        'checkoutAmount',
                        numericAmount.toString(),
                      );
                      setSearchParams(newSearchParams, { replace: true });
                      login();
                    }
                  }}
                  disabled={!isValidAmount}
                >
                  Buy
                </Button>
              </ButtonWrapper>
            </AmountDisplay>
          </Section>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: isMobile ? 'center' : 'space-between',
              gap: '10px',
            }}
          >
            {!isUsdCurrency && isValidAmount && numericAmount && !isMobile && (
              <UsdEstimate $variant='text-secondary'>
                {isEstimatingEstimate
                  ? 'Calculating USD estimate...'
                  : estimateData?.usdAmount || estimateData?.paymentTokenAmount
                    ? `≈ $${parseFloat(estimateData.usdAmount || estimateData.paymentTokenAmount).toFixed(2)} USD`
                    : null}
              </UsdEstimate>
            )}
            <CashbackInfo />
          </div>

          <Line />
          {/* Additional Info */}

          <Section style={{ flexDirection: 'row', gap: '48px' }}>
            <InfoRow>
              <ParagraphSmallText
                $variant='text-secondary'
                style={{ fontWeight: 500 }}
              >
                Expiration
              </ParagraphSmallText>
              <ParagraphMediumText $variant='text-regular'>
                {card.expiration || 'N/A'}
              </ParagraphMediumText>
            </InfoRow>
            {card.country && (
              <InfoRow>
                <ParagraphSmallText
                  $variant='text-secondary'
                  style={{ fontWeight: 500 }}
                >
                  Country
                </ParagraphSmallText>
                <ParagraphMediumText $variant='text-regular'>
                  {card.country}
                </ParagraphMediumText>
              </InfoRow>
            )}
          </Section>
          <Line />
          {/* Redemption Instructions */}
          {card.redemption_instruction && (
            <ExpandableSection $isFirst={true}>
              <ExpandableSummary>Redemption Instructions</ExpandableSummary>
              <ExpandableContent>
                {card.redemption_instruction}
              </ExpandableContent>
            </ExpandableSection>
          )}

          {/* Terms & Conditions */}
          {card.term_and_conditions && (
            <ExpandableSection $isFirst={!card.redemption_instruction}>
              <ExpandableSummary>Terms & Conditions</ExpandableSummary>
              <ExpandableContent>
                {isUrl(card.term_and_conditions) ? (
                  <TermsLink
                    href={card.term_and_conditions}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    View Terms & Conditions
                  </TermsLink>
                ) : (
                  card.term_and_conditions
                )}
              </ExpandableContent>
            </ExpandableSection>
          )}
        </ContentSection>
      </CardContainer>
    </Container>
  );
};

export default SpendGiftCardDetail;
